
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  let closeTimeout: NodeJS.Timeout;

  const handleMouseEnter = () => {
    clearTimeout(closeTimeout);
    setIsServicesOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeout = setTimeout(() => {
      setIsServicesOpen(false);
    }, 300);
  };

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${
      isScrolled || !isHome ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      <nav className="container mx-auto px-4 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center touch-manipulation">
            <img 
              src="https://public.readdy.ai/ai/img_res/c3013058-1479-428a-9f90-d7b3de3d7f4d.png" 
              alt="NWA Gutter Guards Logo" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <Link to="/" className={`text-sm font-medium transition-colors hover:text-primary-600 ${
              isScrolled || !isHome ? 'text-gray-700' : 'text-white'
            }`}>Home</Link>
            <Link to="/about" className={`text-sm font-medium transition-colors hover:text-primary-600 ${
              isScrolled || !isHome ? 'text-gray-700' : 'text-white'
            }`}>About</Link>
            <div 
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button className={`text-sm font-medium transition-colors hover:text-primary-600 flex items-center ${
                isScrolled || !isHome ? 'text-gray-700' : 'text-white'
              }`}>
                Services <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              <div className={`absolute top-full left-0 mt-2 w-64 bg-white shadow-lg rounded-lg transition-all duration-300 ${
                isServicesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <Link to="/services/gutter-guard-installation" className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">Gutter Guard Installation</Link>
                <Link to="/services/stone-coated-gutter-cap" className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">Stone Coated Gutter Cap</Link>
                <Link to="/services/premium-gutter-guards" className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">Premium Gutter Guards</Link>
                <Link to="/services/leaf-guards" className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">Brush & DIY Leaf Guards</Link>
                <Link to="/services/seamless-gutters" className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">Seamless Gutters</Link>
                <Link to="/services/french-drains" className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">French Drains & Tree Protection</Link>
                <Link to="/services/gutter-cleaning" className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">Gutter Cleaning & Repair</Link>
                <Link to="/services/costs-estimates" className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">Costs & Estimates</Link>
              </div>
            </div>
            <Link to="/contact" className={`text-sm font-medium transition-colors hover:text-primary-600 ${
              isScrolled || !isHome ? 'text-gray-700' : 'text-white'
            }`}>Contact</Link>
            <a href="tel:479-337-7565" className={`text-sm font-medium flex items-center ${
              isScrolled || !isHome ? 'text-gray-700' : 'text-white'
            }`}>
              <i className="ri-phone-line mr-2"></i> (479) 337-7565
            </a>
            <Link to="/contact" className="bg-accent-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-accent-700 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl">
              Free Quote
            </Link>
          </div>

          {/* Mobile Right Side - Phone + Menu */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Mobile Click-to-Call Button - Always Visible */}
            <a 
              href="tel:479-337-7565"
              className={`w-12 h-12 flex items-center justify-center rounded-lg transition-all touch-manipulation ${
                isScrolled || !isHome 
                  ? 'bg-accent-600 text-white hover:bg-accent-700' 
                  : 'bg-white/90 text-accent-600 hover:bg-white'
              }`}
              aria-label="Call NWA Gutter Guards"
            >
              <i className="ri-phone-fill text-xl"></i>
            </a>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`w-12 h-12 flex items-center justify-center rounded-lg transition-colors touch-manipulation ${
                isScrolled || !isHome ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu - Touch Optimized */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-2 bg-white rounded-lg shadow-xl border border-gray-100">
            <Link 
              to="/" 
              className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors border-b border-gray-100 touch-manipulation"
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors border-b border-gray-100 touch-manipulation"
            >
              About
            </Link>
            <div className="border-b border-gray-100">
              <div className="px-6 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wider bg-primary-50/50">
                Services
              </div>
              <Link 
                to="/services/gutter-guard-installation" 
                className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors touch-manipulation"
              >
                Gutter Guard Installation
              </Link>
              <Link 
                to="/services/stone-coated-gutter-cap" 
                className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors touch-manipulation"
              >
                Stone Coated Gutter Cap
              </Link>
              <Link 
                to="/services/premium-gutter-guards" 
                className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors touch-manipulation"
              >
                Premium Gutter Guards
              </Link>
              <Link 
                to="/services/seamless-gutters" 
                className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors touch-manipulation"
              >
                Seamless Gutters
              </Link>
              <Link 
                to="/services/gutter-cleaning" 
                className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors touch-manipulation"
              >
                Gutter Cleaning & Repair
              </Link>
              <Link 
                to="/services/french-drains" 
                className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors touch-manipulation"
              >
                French Drains
              </Link>
              <Link 
                to="/services/costs-estimates" 
                className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors border-b border-gray-100 touch-manipulation"
              >
                Costs & Estimates
              </Link>
            </div>
            <Link 
              to="/contact" 
              className="block px-6 py-4 text-base text-gray-700 hover:bg-primary-50 active:bg-primary-100 transition-colors border-b border-gray-100 touch-manipulation"
            >
              Contact
            </Link>
            
            {/* Prominent CTA in Mobile Menu */}
            <div className="px-4 py-4">
              <Link 
                to="/contact" 
                className="flex items-center justify-center w-full bg-gradient-to-r from-accent-600 to-accent-700 text-white px-6 py-4 rounded-lg text-base font-semibold hover:from-accent-700 hover:to-accent-800 active:from-accent-800 active:to-accent-900 transition-all shadow-lg touch-manipulation min-h-[56px]"
              >
                <i className="ri-message-3-line mr-2"></i> Get Free Quote
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
